import * as React from 'react';
import { Theme, StyleRules } from '@material-ui/core/styles';
import withStyles, { ClassNameMap } from '@material-ui/core/styles/withStyles';
import {
  Input,
  Button,
  IconButton,
  TextField,
  Paper,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@material-ui/core';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
} from '@material-ui/icons';
import Dropzone, { DropFileEventHandler } from 'react-dropzone';
import { grey, red, green } from '@material-ui/core/colors';
import classNames from 'classnames';

import './Uploader.css';

import {
  UploadFileForSearch,
  UploadFileState,
  UploadFilePattern,
  uploadFilePatterns,
} from '../modules/uploadForSearch';

interface Props {
  classes: ClassNameMap;
  index: number;
  accept?: string[];
  file: UploadFileForSearch | null;
  fileState: UploadFileState;
  filePattern: string;
  onChangeFile: (index: number, file: File) => void;
  onChangePattern: (index: number, pattern: UploadFilePattern) => void;
  onClearFile: (index: number) => void;
  onError: (index: number, error: string) => void;
}

const style = (theme: Theme): StyleRules => ({
  root: {
    flexGrow: 1,
  },
  textFiled: {
    width: '100%',
    marginLeft: theme.spacing.unit,
    marginRight: theme.spacing.unit,
  },
  paper: {
    padding: theme.spacing.unit * 2,
  },
  paperAccepted: {
    backgroundColor: green[100],
  },
  paperDenied: {
    backgroundColor: theme.palette.background.paper,
  },
  input: {
    display: 'none',
  },
  dropzone: {
    marginTop: theme.spacing.unit * 3,
    marginBottom: theme.spacing.unit,
    padding: theme.spacing.unit * 2,
    width: '100%',
    height: 250,
    backgroundColor: grey[100],
    border: 'dashed',
    borderColor: grey[400],
  },
  stripes: {
    border: 'solid',
    borderColor: grey[400],
    // tslint:disable-next-line:max-line-length
    backgroundImage: `repeating-linear-gradient(-45deg, ${grey[100]}, ${grey[100]} 25px, ${grey[400]} 25px, ${grey[400]} 50px)`,
    animation: 'progress 2s linear infinite !important',
    backgroundSize: '150% 100%',
  },
  rejectStripes: {
    border: 'solid',
    borderColor: grey[400],
    // tslint:disable-next-line:max-line-length
    backgroundImage: `repeating-linear-gradient(-45deg, ${red[100]}, ${red[100]} 25px, ${red[400]} 25px, ${red[400]} 50px)`,
    animation: 'progress 2s linear infinite !important',
    backgroundSize: '150% 100%',
  },
  dropzoneContent: {
    textAlign: 'center',
    top: '30%',
    position: 'relative',
  },
  dropzoneText: {
    fontSize: theme.typography.fontSize * 1.5,
    color: grey[500],
  },
  dropzoneLoading: {
    position: 'relative',
    top: '50%',
    left: '50%',
    marginTop: -25,
    marginLeft: -25,
  },
  buttonLoading: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -10,
    marginLeft: -10,
  },
  uploadIcon: {
    fontSize: theme.typography.fontSize * 4,
    color: grey[500],
  },
  formControl: {
    display: 'flex',
    flexWrap: 'wrap',
    margin: theme.spacing.unit,
  },
  uploadControl: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },
  uploadButton: {
    width: 120,
  },
  wrapper: {
    position: 'relative',
  },
  clearButton: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
});

class UploaderForSearch extends React.Component<Props> {
  handleChangePattern = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { onChangePattern, index } = this.props;
    const { value } = event.target;
    onChangePattern(index, value as UploadFilePattern);
  }

  handleChangeFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { onChangeFile, index } = this.props;
    const { files } = event.target;
    const file = files && files.item(0);
    if (file) {
      onChangeFile(index, file);  // 親のonChangeFileが呼ばれる
    }
  }

  handleDropAccepted: DropFileEventHandler = (files) => {
    const { onChangeFile, index } = this.props;
    const file = files[0];
    onChangeFile(index, file);
  }

  handleDropRejected: DropFileEventHandler = (_) => {
    const { onError, index } = this.props;
    onError(index, 'ファイルタイプが異なります');
  }

  handleClearFile = () => {
    const { onClearFile, index } = this.props;
    onClearFile(index);
  }

  render() {
    const { accept, classes, file, fileState, filePattern, index } = this.props;
    const loading = fileState === 'uploading';
    const fileName = (file && file.fileName) || '';
    const acceptTypes = accept && accept.join(',');
    return (
      <div className={classes.root} >
        {fileName &&
          <div className={classes.wrapper} hidden>
            <IconButton className={classes.clearButton} onClick={this.handleClearFile}>
              <DeleteIcon />
            </IconButton>
          </div>
        }
        <Paper
          className={
            classNames(classes.paper, file ? classes.paperAccepted : classes.paperDenied)
          }
        >
          <Grid
            container
            direction="column"
          >
            <Grid item hidden>
              <FormControl className={classes.formControl}>
                <InputLabel htmlFor="file-pattern">ファイルパターン</InputLabel>
                <Select
                  value={filePattern}
                  inputProps={{
                    name: 'filePattern',
                    id: 'file-pattern',
                  }}
                  onChange={this.handleChangePattern}
                >
                  {uploadFilePatterns.map((pattern, i) => (
                    <MenuItem key={i} value={pattern}>{pattern}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item>
              <div className={classes.uploadControl}>
                <TextField
                  className={classes.textFiled}
                  label="ファイル名"
                  value={fileName}
                  inputProps={{
                    readOnly: true,
                  }}
                />
                <Input
                  className={classes.input}
                  id={`contained-button-file-${index}`}
                  type="file"
                  onChange={this.handleChangeFile}
                  inputProps={{
                    accept: acceptTypes,
                  }}
                />
                <label htmlFor={`contained-button-file-${index}`}>
                  <Button
                    variant="contained"
                    color="primary"
                    component="span"
                    disabled={loading}
                    className={classes.uploadButton}
                  >
                    アップロード
                    {loading &&
                      <CircularProgress
                        className={classes.buttonLoading}
                        size={20}
                        color="secondary"
                      />
                    }
                  </Button>
                </label>
              </div>
            </Grid>
            <Grid item>
              {loading ?
                <div className={classes.dropzone}>
                  <div className={classes.dropzoneLoading}>
                    <CircularProgress size={50} color="secondary" />
                  </div>
                </div> :
                <Dropzone
                  className={classes.dropzone}
                  accept={acceptTypes}
                  acceptClassName={classes.stripes}
                  rejectClassName={classes.rejectStripes}
                  multiple={false}
                  disablePreview
                  onDropAccepted={this.handleDropAccepted}
                  onDropRejected={this.handleDropRejected}
                >
                  <div className={classes.dropzoneContent}>
                    <Typography component="p" className={classes.dropzoneText}>
                      ファイルをドロップしてください
                    </Typography>
                    <CloudUploadIcon className={classes.uploadIcon} />
                  </div>
                </Dropzone>
              }
            </Grid>
          </Grid>
        </Paper>
      </div>
    );
  }
}

export default withStyles(style)(UploaderForSearch);



// WEBPACK FOOTER //
// ./src/components/UploaderForRiskJudge.tsx